import React from 'react';
import { 
  LuChevronRight, 
  LuFileText, 
  LuInfo 
} from 'react-icons/lu';

// Safe, version-independent status indicator
import { FiCheckCircle } from 'react-icons/fi';

export default function MaintenanceAuditDashboard() {
  // Data for Recent Maintenance Completed Table
  const recentMaintenance = [
    { equipment: 'Extractor Fan – Kitchen', location: 'Kitchen', completedOn: '18 Jul 2026', type: 'Planned' },
    { equipment: 'Walk-in Freezer', location: 'Kitchen', completedOn: '17 Jul 2026', type: 'Planned' },
    { equipment: 'POS Terminal', location: 'Front of House', completedOn: '16 Jul 2026', type: 'Preventive' },
    { equipment: 'Combi Oven', location: 'Kitchen', completedOn: '15 Jul 2026', type: 'Preventive' },
    { equipment: 'Floor Scrubber', location: 'Front of House', completedOn: '14 Jul 2026', type: 'Corrective' },
  ];

  // Data for Key Documents & Evidence List
  const documents = [
    { name: 'Equipment Register', date: 'Updated 18 Jul 2026' },
    { name: 'Service & Maintenance Schedule', date: 'Updated 12 Jul 2026' },
    { name: 'Maintenance Policy', date: 'Updated 01 Jul 2026' },
    { name: 'Service Contracts', date: 'Updated 18 Jul 2026' },
    { name: 'Equipment Manual Log', date: 'Updated 10 Jul 2026' },
    { name: 'Statutory Inspection Certificates', date: 'Updated 18 Jul 2026' },
  ];

  // Data for Key Information for Inspector List
  const inspectorChecks = [
    'All equipment records are available',
    'Service contracts are active',
    'Maintenance records are complete',
    'Statutory inspections are up to date',
    'Out of service items are identified',
    'Engineers & service providers information available',
    'Equipment manuals are available',
  ];

  return (
    <div className="w-full bg-slate-50 p-6 font-sans flex flex-col gap-6">
      
      {/* 3-Column Card Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-[1800px] mx-auto items-stretch w-full">
        
        {/* Card 1: Recent Maintenance Completed */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[400px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-4">
              Recent Maintenance Completed
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-indigo-50/40 text-[9px] font-bold text-indigo-950/70 tracking-wider">
                    <th className="p-2.5 rounded-l-lg w-[38%]">EQUIPMENT</th>
                    <th className="p-2.5">LOCATION</th>
                    <th className="p-2.5">COMPLETED ON</th>
                    <th className="p-2.5 rounded-r-lg">TYPE</th>
                  </tr>
                </thead>
                <tbody className="text-[11px] font-semibold text-slate-700 divide-y divide-slate-50">
                  {recentMaintenance.map((row, index) => (
                    <tr key={index} className="hover:bg-slate-50/40 transition-colors">
                      <td className="p-2.5 font-bold text-slate-800">{row.equipment}</td>
                      <td className="p-2.5 text-slate-600">{row.location}</td>
                      <td className="p-2.5 text-slate-900 font-medium">{row.completedOn}</td>
                      <td className="p-2.5 text-slate-600 font-normal">{row.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View all completed</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

        {/* Card 2: Key Documents & Evidence */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[400px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-5">
              Key Documents & Evidence
            </h2>
            <div className="flex flex-col gap-3.5">
              {documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between text-[11px] font-semibold">
                  <div className="flex items-center gap-2.5 text-indigo-950">
                    <LuFileText className="text-indigo-600 text-sm flex-shrink-0" />
                    <span>{doc.name}</span>
                  </div>
                  <span className="text-slate-400 font-normal">{doc.date}</span>
                </div>
              ))}
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View all documents</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

        {/* Card 3: Key Information for Inspector */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between min-h-[400px]">
          <div>
            <h2 className="text-[14px] font-bold text-indigo-950 mb-5">
              Key Information for Inspector
            </h2>
            <div className="flex flex-col gap-3">
              {inspectorChecks.map((text, index) => (
                <div key={index} className="flex items-start justify-between text-[11px] font-semibold group">
                  <div className="flex items-start gap-2.5 text-slate-800 pr-4">
                    <FiCheckCircle  className="text-emerald-600 text-sm mt-0.5 flex-shrink-0" />
                    <span className="leading-tight">{text}</span>
                  </div>
                  <span className="text-slate-400 font-normal whitespace-nowrap pt-0.5">Up to date</span>
                </div>
              ))}
            </div>
          </div>
          <button className="mt-4 flex items-center justify-start gap-1 w-fit text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors py-1">
            <span>View equipment register</span>
            <LuChevronRight className="text-xs stroke-[2.5]" />
          </button>
        </div>

      </div>

      {/* Bottom Informational Footer Alert Bar */}
      <div className="w-full max-w-[1800px] mx-auto bg-indigo-50/40 border border-indigo-100/50 rounded-xl px-4 py-3 flex items-center gap-2.5">
        <LuInfo className="text-blue-600 text-sm flex-shrink-0" />
        <p className="text-[10.5px] font-medium text-slate-500 tracking-wide">
          All equipment and maintenance records are time stamped and securely stored for compliance and audit purposes.
        </p>
      </div>

    </div>
  );
}
